import {Plus} from 'lucide-react'
import type {BoardColumn, Task, TaskStatus} from '../../types'
import {useApp} from '../../context/AppContext'
import KanbanCard from './KanbanCard'

interface KanbanColumnProps {
  column: BoardColumn
  tasks: Task[]
  onAddTask: (status: TaskStatus) => void
}

export default function KanbanColumn({column, tasks, onAddTask}: KanbanColumnProps) {
  const {apiMoveTask} = useApp()
  const isOverLimit = column.limit !== undefined && tasks.length > column.limit

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    ;(e.currentTarget as HTMLElement).classList.add('drag-over')
  }

  const handleDragLeave = (e: React.DragEvent) => {
    ;(e.currentTarget as HTMLElement).classList.remove('drag-over')
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    ;(e.currentTarget as HTMLElement).classList.remove('drag-over')
    const taskId = e.dataTransfer.getData('taskId')
    if (taskId) {
      await apiMoveTask(taskId, column.status)
    }
  }

  return (
    <div className='devops-kanban-column flex flex-col min-w-[280px] w-[280px] md:min-w-[300px] md:w-[300px] shrink-0'>
      <div className='flex items-center justify-between px-1 mb-3'>
        <div className='flex items-center gap-2'>
          <div className='w-2.5 h-2.5 rounded-full' style={{backgroundColor: column.color}} />
          <h3 className='text-sm font-semibold text-slate-700'>{column.title}</h3>
          <span
            className={`text-xs font-medium px-1.5 py-0.5 rounded-md ${
              isOverLimit ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'
            }`}
          >
            {tasks.length}
            {column.limit !== undefined && `/${column.limit}`}
          </span>
        </div>
        <button
          onClick={() => onAddTask(column.status)}
          className='p-1 rounded-lg text-slate-400 hover:text-brand-500 hover:bg-brand-50 transition-colors'
          aria-label={`Add task to ${column.title}`}
        >
          <Plus size={16} />
        </button>
      </div>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className='flex-1 space-y-2.5 p-1 rounded-xl min-h-[200px] transition-colors duration-200'
      >
        {tasks.map((task, index) => (
          <div
            key={task.id}
            className='animate-slide-in-up'
            style={{animationDelay: `${index * 30}ms`}}
          >
            <KanbanCard task={task} />
          </div>
        ))}
        {tasks.length === 0 && (
          <div className='flex items-center justify-center h-32 rounded-xl border border-dashed border-slate-300 bg-white/50 text-xs text-slate-400'>
            Arraste tarefas aqui
          </div>
        )}
      </div>
    </div>
  )
}
